import React, { useState } from 'react';

const TaskTapVisualFlow = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      id: 0,
      phase: "BROADCAST",
      title: "Task Posted to Slack",
      description: "Task appears in channel with claim button",
      visual: "📢",
      color: "green",
      details: [
        "POST /tasktap/broadcast",
        "Unique task ID generated",
        "Message posted to Slack channel",
        "Stored in D1 database"
      ]
    },
    {
      id: 1,
      phase: "CLAIM",
      title: "Instant Claim & Delete",
      description: "First click wins, message vanishes",
      visual: "⚡",
      color: "yellow",
      details: [
        "User clicks 'Claim Task'",
        "Message DELETES instantly",
        "No duplicate claims possible",
        "DM sent to claimer"
      ]
    },
    {
      id: 2,
      phase: "WORK",
      title: "Task Execution",
      description: "Worker completes the actual task",
      visual: "💪",
      color: "blue",
      details: [
        "Worker has task in DM",
        "Complete/Release/Revise buttons",
        "Does the actual work",
        "Clicks Complete when done"
      ]
    },
    {
      id: 3,
      phase: "REVIEW",
      title: "Quality Check",
      description: "Human or AI reviews completion",
      visual: "🔍",
      color: "purple",
      details: [
        "Review request in channel",
        "Approve & Pay button",
        "Reject button",
        "AI Review option (future)"
      ]
    },
    {
      id: 4,
      phase: "PAYMENT",
      title: "Instant Payment",
      description: "Money transferred, dashboard updated",
      visual: "💰",
      color: "green",
      details: [
        "Payment recorded in database",
        "Worker notified via DM",
        "Dashboard auto-updates",
        "Earnings immediately visible"
      ]
    }
  ];

  const currentStepData = steps[currentStep];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500">
            TaskTap Flow
          </h1>
          <p className="text-xl text-gray-400">
            From broadcast to payment in 5 steps
          </p>
        </div>

        {/* Progress Bar */}
        <div className="flex justify-between mb-12">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className="flex-1 cursor-pointer"
              onClick={() => setCurrentStep(index)}
            >
              <div className="flex flex-col items-center">
                <div
                  className={`text-4xl mb-2 transition-all ${
                    currentStep === index ? 'scale-125' : 'opacity-50'
                  }`}
                >
                  {step.visual}
                </div>
                <div
                  className={`h-2 w-full rounded transition-all ${
                    currentStep >= index
                      ? `bg-${step.color}-500`
                      : 'bg-gray-700'
                  }`}
                  style={{
                    backgroundColor:
                      currentStep >= index
                        ? step.color === 'green'
                          ? '#10b981'
                          : step.color === 'yellow'
                          ? '#f59e0b'
                          : step.color === 'blue'
                          ? '#3b82f6'
                          : step.color === 'purple'
                          ? '#8b5cf6'
                          : '#10b981'
                        : '#374151'
                  }}
                />
                <div className={`text-xs mt-2 ${
                  currentStep === index ? 'text-white' : 'text-gray-500'
                }`}>
                  {step.phase}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Current Step Details */}
        <div className="bg-gray-900 border-2 border-gray-700 rounded-lg p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold mb-2" style={{
                color: currentStepData.color === 'green' ? '#10b981'
                  : currentStepData.color === 'yellow' ? '#f59e0b'
                  : currentStepData.color === 'blue' ? '#3b82f6'
                  : currentStepData.color === 'purple' ? '#8b5cf6'
                  : '#10b981'
              }}>
                {currentStepData.title}
              </h2>
              <p className="text-gray-400 text-lg">
                {currentStepData.description}
              </p>
            </div>
            <div className="text-6xl opacity-20">
              {currentStepData.visual}
            </div>
          </div>

          <div className="space-y-3">
            {currentStepData.details.map((detail, index) => (
              <div key={index} className="flex items-center">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3" />
                <span className="text-gray-300">{detail}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mb-8">
          <button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            className="px-6 py-3 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
            disabled={currentStep === 0}
          >
            ← Previous
          </button>
          <button
            onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
            className="px-6 py-3 bg-green-500 text-black rounded-lg hover:bg-green-400 transition-colors"
            disabled={currentStep === steps.length - 1}
          >
            Next →
          </button>
        </div>

        {/* The Magic Box */}
        <div className="bg-gradient-to-r from-green-900/20 to-blue-900/20 border border-green-400 rounded-lg p-6 mb-8">
          <h3 className="text-2xl font-bold text-green-400 mb-4">
            ⚡ The Secret Sauce: Instant Claim & Delete
          </h3>
          <p className="text-gray-300 mb-4">
            The key innovation that makes TaskTap work is the instant deletion upon claim.
            No race conditions, no double claims, no confusion.
          </p>
          <div className="bg-black/50 rounded p-4 font-mono text-sm">
            <div className="text-green-400"># When claim button is clicked:</div>
            <div className="text-gray-400">1. DELETE message from channel (instant)</div>
            <div className="text-gray-400">2. UPDATE database (task claimed)</div>
            <div className="text-gray-400">3. SEND DM to claimer (with details)</div>
            <div className="text-yellow-400 mt-2">Result: First click always wins!</div>
          </div>
        </div>

        {/* Live Stats (Mock) */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-green-400">$77</div>
            <div className="text-gray-500">Total Earned</div>
          </div>
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-yellow-400">&lt; 500ms</div>
            <div className="text-gray-500">Claim Speed</div>
          </div>
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-blue-400">100%</div>
            <div className="text-gray-500">Completion Rate</div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <p className="text-gray-400 mb-4">
            This flow is live and working. The infrastructure is built.
          </p>
          <p className="text-2xl text-green-400 font-bold">
            Ready to distribute your first real task?
          </p>
        </div>
      </div>
    </div>
  );
};

export default TaskTapVisualFlow;